'use client';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useRef, useState } from 'react';
import { adminService } from '@/lib/services/adminService';
import { managerService } from '@/lib/services/managerService';
import Icon from '@/components/ui/AppIcon';

const Header = () => {
  const { user, signOut, role } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Treat a link as active when the current path matches it (or is nested under it).
  const isActive = (href: string) =>
    pathname === href || (href !== '/homepage' && pathname?.startsWith(`${href}/`));

  // Desktop nav link styling with active state.
  const navLinkClass = (href: string) =>
    `transition-colors font-medium ${
      isActive(href)
        ? 'text-primary font-semibold border-b-2 border-primary pb-0.5'
        : 'text-foreground hover:text-primary border-b-2 border-transparent pb-0.5'
    }`;

  // Mobile nav link styling with active state.
  const mobileLinkClass = (href: string) =>
    `transition-colors font-medium py-3 px-4 rounded-lg touch-manipulation ${
      isActive(href)
        ? 'text-primary font-semibold bg-primary/10'
        : 'text-foreground hover:text-primary hover:bg-gray-50'
    }`;

  const mobilePortalLinkClass = (href: string) =>
    `transition-colors font-semibold py-3 px-4 rounded-lg touch-manipulation ${
      isActive(href)
        ? 'text-primary bg-primary/10'
        : 'text-primary hover:text-primary/80 hover:bg-primary/5'
    }`;
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isAgent, setIsAgent] = useState(false);
  const [agentCanViewLeads, setAgentCanViewLeads] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target as Node)) {
        setShowQuickActions(false);
      }
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Derived, dynamic details for the currently logged-in user.
  const displayName: string =
    userProfile?.full_name?.trim() || user?.email?.split('@')?.[0] || 'Account';
  const displayEmail: string = userProfile?.email || user?.email || '';
  const roleLabel: string = (userProfile?.role || role || 'customer')
    .toString()
    .replace(/_/g, ' ');
  const initials: string = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0])
    .join('')
    .toUpperCase();

  useEffect(() => {
    const checkAdmin = async () => {
      if (user) {
        try {
          const roles = await adminService.checkRoleByUserId(user.id);
          setIsAdmin(roles.isAdmin);
          setIsManager(roles.isManager);
          setIsAgent(roles.isAgent);

          // Agents only see the "My Leads" quick action when they have been
          // granted leads-view permission; otherwise it 404s/bounces to home.
          if (roles.isAgent && !roles.isAdmin && !roles.isManager) {
            const canViewLeads = await managerService.hasPermission('leads', 'view');
            setAgentCanViewLeads(canViewLeads);
          } else {
            setAgentCanViewLeads(false);
          }
          
          const { createClient } = await import('@/lib/supabase/client');
          const supabase = createClient();
          const { data: profile } = await supabase?.from('user_profiles')?.select('full_name, email, phone, role')?.eq('id', user?.id)?.maybeSingle();
          
          if (profile) {
            setUserProfile(profile);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
          setIsManager(false);
          setIsAgent(false);
          setAgentCanViewLeads(false);
        }
      } else {
        setIsAdmin(false);
        setIsManager(false);
        setIsAgent(false);
        setAgentCanViewLeads(false);
        setUserProfile(null);
      }
    };
    checkAdmin();
  }, [user]);

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    // Close any open menus so the UI immediately reflects the logout flow
    setShowQuickActions(false);
    setShowAccountMenu(false);
    setIsMenuOpen(false);
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    } finally {
      // Hard navigation guarantees all in-memory auth state is dropped and the
      // header re-renders in its signed-out state, even if signOut() hit an error.
      window.location.href = '/homepage';
    }
  };

  const handleQuickActionNav = (href: string) => {
    setShowQuickActions(false);
    router.push(href);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <Icon name="CubeIcon" size={20} className="sm:w-6 sm:h-6 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <div className="text-sm sm:text-xl font-bold font-montserrat text-foreground leading-tight">
                Containers by Take a Zip
              </div>
              <div className="text-[10px] sm:text-xs text-primary">Turn-Key Food Solutions</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7 whitespace-nowrap">
            <Link href="/homepage" className={navLinkClass('/homepage')}>
              Home
            </Link>
            <Link href="/models-pricing" className={navLinkClass('/models-pricing')}>
              Models & Pricing
            </Link>
            {user && (
              <Link href="/merchandise-store" className={navLinkClass('/merchandise-store')}>
                Merchandise
              </Link>
            )}
            {user && !isAdmin && !isManager && !isAgent && (
              <>
                <Link href="/customer-portal" className={navLinkClass('/customer-portal')}>
                  My Portal
                </Link>
                <Link href="/customer-dashboard" className={navLinkClass('/customer-dashboard')}>
                  Dashboard
                </Link>
                {/* Quick Actions Dropdown for Customer */}
                <div className="relative" ref={quickActionsRef}>
                  <button
                    onClick={() => setShowQuickActions(!showQuickActions)}
                    className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium"
                  >
                    Quick Actions
                    <Icon name="ChevronDownIcon" size={16} />
                  </button>
                  {showQuickActions && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <button onClick={() => handleQuickActionNav('/notifications')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Notifications</button>
                      <button onClick={() => handleQuickActionNav('/project-status')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Project Status</button>
                      <button onClick={() => handleQuickActionNav('/billing')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Billing</button>
                      <button onClick={() => handleQuickActionNav('/book-appointment')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Book Appointment</button>
                      <button onClick={() => handleQuickActionNav('/messages')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Messages</button>
                      <button onClick={() => handleQuickActionNav('/order-tracking')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Order Tracking</button>
                      <button onClick={() => handleQuickActionNav('/notification-settings')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Settings</button>
                    </div>
                  )}
                </div>
              </>
            )}
            {isAgent && !isAdmin && !isManager && (
              <>
                <Link href="/agent-portal" className={navLinkClass('/agent-portal')}>
                  Agent Portal
                </Link>
                {/* Quick Actions Dropdown for Agent */}
                <div className="relative" ref={quickActionsRef}>
                  <button
                    onClick={() => setShowQuickActions(!showQuickActions)}
                    className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium"
                  >
                    Quick Actions
                    <Icon name="ChevronDownIcon" size={16} />
                  </button>
                  {showQuickActions && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      {agentCanViewLeads && (
                        <button onClick={() => handleQuickActionNav('/leads-dashboard')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">My Leads</button>
                      )}
                      <button onClick={() => handleQuickActionNav('/messages')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Messages</button>
                      <button onClick={() => handleQuickActionNav('/notifications')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Notifications</button>
                    </div>
                  )}
                </div>
              </>
            )}
            {isManager && !isAdmin && (
              <>
                <Link href="/manager-dashboard" className={navLinkClass('/manager-dashboard')}>
                  Dashboard
                </Link>
                <Link href="/manager-portal" className={navLinkClass('/manager-portal')}>
                  Manager Portal
                </Link>
                {/* Quick Actions Dropdown for Manager */}
                <div className="relative" ref={quickActionsRef}>
                  <button
                    onClick={() => setShowQuickActions(!showQuickActions)}
                    className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium"
                  >
                    Quick Actions
                    <Icon name="ChevronDownIcon" size={16} />
                  </button>
                  {showQuickActions && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <button onClick={() => handleQuickActionNav('/admin/quotes')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Quotes</button>
                      <button onClick={() => handleQuickActionNav('/admin/leads')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Leads</button>
                      <button onClick={() => handleQuickActionNav('/admin/customers')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Customers</button>
                      <button onClick={() => handleQuickActionNav('/messages')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Messages</button>
                      <button onClick={() => handleQuickActionNav('/admin/documents')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Documents</button>
                      <button onClick={() => handleQuickActionNav('/admin/analytics')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Analytics</button>
                      <button onClick={() => handleQuickActionNav('/reports')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Reports</button>
                      <button onClick={() => handleQuickActionNav('/admin/profile')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Profile Settings</button>
                    </div>
                  )}
                </div>
              </>
            )}
            {isAdmin && (
              <>
                <Link href="/admin" className={navLinkClass('/admin')}>
                  Dashboard
                </Link>
                {isManager && (
                  <Link href="/manager-portal" className={navLinkClass('/manager-portal')}>
                    Manager Portal
                  </Link>
                )}
                {/* Quick Actions Dropdown for Admin */}
                <div className="relative" ref={quickActionsRef}>
                  <button
                    onClick={() => setShowQuickActions(!showQuickActions)}
                    className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium"
                  >
                    Quick Actions
                    <Icon name="ChevronDownIcon" size={16} />
                  </button>
                  {showQuickActions && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <button onClick={() => handleQuickActionNav('/admin/quotes')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Quotes</button>
                      <button onClick={() => handleQuickActionNav('/admin/leads')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Leads</button>
                      <button onClick={() => handleQuickActionNav('/admin/customers')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Customers</button>
                      <button onClick={() => handleQuickActionNav('/admin/models')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Models</button>
                      <button onClick={() => handleQuickActionNav('/admin/documents')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Documents</button>
                      <button onClick={() => handleQuickActionNav('/messages')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Messages</button>
                      <button onClick={() => handleQuickActionNav('/admin/analytics')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Analytics</button>
                      <button onClick={() => handleQuickActionNav('/reports')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Reports</button>
                      <button onClick={() => handleQuickActionNav('/admin/manager-permissions')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Manager Permissions</button>
                      <button onClick={() => handleQuickActionNav('/admin/feature-access-control')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Feature Access Control</button>
                      <button onClick={() => handleQuickActionNav('/admin/testimonials')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Testimonials</button>
                      <button onClick={() => handleQuickActionNav('/admin/notification-logs')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Notification Logs</button>
                      <button onClick={() => handleQuickActionNav('/admin/orders')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Orders & Payments</button>
                      <button onClick={() => handleQuickActionNav('/admin/customer-editor')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Customer Editor</button>
                      <button onClick={() => handleQuickActionNav('/admin/profile')} className="w-full text-left block px-4 py-2 text-foreground hover:bg-gray-50 transition-colors">Profile Settings</button>
                    </div>
                  )}
                </div>
              </>
            )}
            {!user && (
              <>
                <Link href="/about" className={navLinkClass('/about')}>
                  About
                </Link>
                <Link href="/contact" className={navLinkClass('/contact')}>
                  Contact
                </Link>
              </>
            )}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden xl:flex items-center gap-4 flex-shrink-0">
            {user ? (
              <div className="relative" ref={accountMenuRef}>
                <button
                  onClick={() => setShowAccountMenu(!showAccountMenu)}
                  className="flex items-center gap-2.5 rounded-full border border-gray-200 bg-white pl-1.5 pr-3 py-1.5 hover:border-primary/40 hover:bg-gray-50 transition-all"
                  aria-haspopup="true"
                  aria-expanded={showAccountMenu}
                >
                  <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {initials || 'U'}
                  </span>
                  <span className="flex flex-col items-start min-w-0 max-w-[140px]">
                    <span className="text-sm font-semibold text-foreground leading-tight truncate w-full">
                      {displayName}
                    </span>
                    <span className="text-[11px] text-primary capitalize leading-tight truncate w-full">
                      {roleLabel}
                    </span>
                  </span>
                  <Icon name="ChevronDownIcon" size={16} className={`text-muted-foreground flex-shrink-0 transition-transform ${showAccountMenu ? 'rotate-180' : ''}`} />
                </button>

                {showAccountMenu && (
                  <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    {/* User details */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <span className="w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-base font-bold flex-shrink-0">
                          {initials || 'U'}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                          {displayEmail && (
                            <p className="text-xs text-muted-foreground truncate">{displayEmail}</p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 rounded-full px-2.5 py-1 capitalize">
                          <Icon name="UserIcon" size={12} />
                          {roleLabel}
                        </span>
                        {userProfile?.phone && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground bg-gray-100 rounded-full px-2.5 py-1">
                            <Icon name="PhoneIcon" size={12} />
                            {userProfile.phone}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={handleSignOut}
                      disabled={signingOut}
                      className="w-full inline-flex items-center gap-2 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {signingOut ? (
                        <>
                          <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                          Signing Out...
                        </>
                      ) : (
                        <>
                          <Icon name="ArrowRightOnRectangleIcon" size={18} />
                          Sign Out
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  href="/login"
                  className="text-foreground hover:text-primary transition-colors font-medium"
                >
                  Sign In
                </Link>
                <Link
                  href="/get-a-quote"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-6 py-2.5 hover:bg-primary/90 transition-all text-sm"
                >
                  Get a Quote
                  <Icon name="ArrowRightIcon" size={18} />
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="xl:hidden p-2 text-foreground hover:text-primary transition-colors touch-manipulation"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={28} />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="xl:hidden py-6 border-t border-gray-200 bg-white">
            <nav className="flex flex-col gap-2">
              <Link
                href="/homepage"
                className={mobileLinkClass('/homepage')}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              {!user && (
                <Link
                  href="/about"
                  className={mobileLinkClass('/about')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </Link>
              )}
              <Link
                href="/models-pricing"
                className={mobileLinkClass('/models-pricing')}
                onClick={() => setIsMenuOpen(false)}
              >
                Models & Pricing
              </Link>
              {!user && (
                <Link
                  href="/contact"
                  className={mobileLinkClass('/contact')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </Link>
              )}
              {user && (
                <Link
                  href="/merchandise-store"
                  className={mobileLinkClass('/merchandise-store')}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Merchandise Store
                </Link>
              )}
              {user && !isAdmin && !isManager && !isAgent && (
                <>
                  <Link
                    href="/customer-dashboard"
                    className={mobilePortalLinkClass('/customer-dashboard')}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    href="/customer-portal"
                    className={mobilePortalLinkClass('/customer-portal')}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    My Portal
                  </Link>
                </>
              )}
              {isAgent && !isAdmin && !isManager && (
                <>
                  {agentCanViewLeads && (
                    <Link
                      href="/leads-dashboard"
                      className={mobilePortalLinkClass('/leads-dashboard')}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      My Leads
                    </Link>
                  )}
                  <Link
                    href="/messages"
                    className={mobilePortalLinkClass('/messages')}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Messages
                  </Link>
                  <Link
                    href="/notifications"
                    className={mobilePortalLinkClass('/notifications')}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Notifications
                  </Link>
                </>
              )}
              {isManager && !isAdmin && (
                <>
                  <Link
                    href="/manager-dashboard"
                    className={mobilePortalLinkClass('/manager-dashboard')}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    href="/manager-portal"
                    className={mobilePortalLinkClass('/manager-portal')}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Manager Portal
                  </Link>
                </>
              )}
              {isAdmin && (
                <>
                  <Link
                    href="/admin"
                    className={mobilePortalLinkClass('/admin')}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {isManager && (
                    <Link
                      href="/manager-portal"
                      className={mobilePortalLinkClass('/manager-portal')}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Manager Portal
                    </Link>
                  )}
                </>
              )}
              <div className="pt-4 mt-2 border-t border-gray-200 space-y-3">
                {user ? (
                  <>
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl">
                      <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {initials || 'U'}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                        {displayEmail && (
                          <p className="text-xs text-muted-foreground truncate">{displayEmail}</p>
                        )}
                        <span className="inline-block mt-1 text-[11px] font-medium text-primary bg-primary/10 rounded-full px-2 py-0.5 capitalize">
                          {roleLabel}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={handleSignOut}
                      disabled={signingOut}
                      className="w-full inline-flex items-center justify-center gap-2 bg-gray-100 text-foreground font-semibold rounded-full px-6 py-4 hover:bg-gray-200 transition-all touch-manipulation min-h-[48px] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {signingOut ? (
                        <>
                          <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                          Signing Out...
                        </>
                      ) : (
                        <>
                          <Icon name="ArrowRightOnRectangleIcon" size={20} />
                          Sign Out
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <Link
                    href="/login"
                    className="w-full inline-flex items-center justify-center gap-2 bg-gray-100 text-foreground font-semibold rounded-full px-6 py-4 hover:bg-gray-200 transition-all touch-manipulation min-h-[48px]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Icon name="UserIcon" size={20} />
                    Sign In
                  </Link>
                )}
                <Link
                  href="/get-a-quote"
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-6 py-4 hover:bg-primary/90 transition-all touch-manipulation min-h-[48px]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get a Quote
                  <Icon name="ArrowRightIcon" size={20} />
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header